import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertTriangle, Plus, X, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────

const TYP_LABELS = {
  franvaro_utan_anledning: 'Frånvaro utan anledning',
  missad_aktivitet: 'Missad aktivitet',
  regelbrott: 'Regelbrott',
  ovrigt: 'Övrigt',
};

const ALLVARLIGHET_CONFIG = {
  lag:   { label: 'Låg',   className: 'bg-success/20 text-success' },
  medel: { label: 'Medel', className: 'bg-primary/20 text-[#333333]' },
  hog:   { label: 'Hög',   className: 'bg-destructive/20 text-destructive' },
};

const STATUS_CONFIG = {
  oppen:    { label: 'Öppen',    className: 'bg-blue-100 text-blue-700' },
  utreds:   { label: 'Utreds',   className: 'bg-orange-100 text-orange-700' },
  atgardad: { label: 'Åtgärdad', className: 'bg-success/20 text-success' },
  stangd:   { label: 'Stängd',   className: 'bg-muted text-muted-foreground' },
};

const selectCls = 'h-9 w-full px-3 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring';

const Badge = ({ config }) => (
  <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${config.className}`}>
    {config.label}
  </span>
);

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ── Inline KPI ─────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub }) => (
  <Card>
    <CardContent className="p-6">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-3xl font-heading font-black mt-1">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

// ── Inline create form ─────────────────────────────────────────────────────────

const InlineCreateForm = ({ participants, onCreated, onClose }) => {
  const [form, setForm] = useState({
    participant_id: '',
    typ: '',
    allvarlighetsgrad: 'medel',
    beskrivning: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.participant_id) errs.participant_id = 'Välj deltagare';
    if (!form.typ)            errs.typ            = 'Välj typ';
    if (!form.beskrivning.trim()) errs.beskrivning = 'Beskrivning krävs';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const res = await api.post('/avvikelser', {
        participant_id: form.participant_id,
        typ: form.typ,
        beskrivning: form.beskrivning,
        allvarlighetsgrad: form.allvarlighetsgrad,
      });
      toast.success('Avvikelse registrerad');
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte registrera avvikelse');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="deviation-inline-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="dev-participant">Deltagare *</Label>
          <select
            id="dev-participant"
            data-testid="deviation-participant-select"
            value={form.participant_id}
            onChange={e => {
              setForm(f => ({ ...f, participant_id: e.target.value }));
              if (errors.participant_id) setErrors(er => ({ ...er, participant_id: '' }));
            }}
            className={`${selectCls}${errors.participant_id ? ' border-destructive' : ''}`}
          >
            <option value="">Välj deltagare</option>
            {participants.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {errors.participant_id && (
            <p className="text-xs text-destructive" data-testid="deviation-participant-error">{errors.participant_id}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="dev-typ">Typ *</Label>
          <select
            id="dev-typ"
            data-testid="deviation-typ-select"
            value={form.typ}
            onChange={e => {
              setForm(f => ({ ...f, typ: e.target.value }));
              if (errors.typ) setErrors(er => ({ ...er, typ: '' }));
            }}
            className={`${selectCls}${errors.typ ? ' border-destructive' : ''}`}
          >
            <option value="">Välj typ</option>
            {Object.entries(TYP_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          {errors.typ && (
            <p className="text-xs text-destructive" data-testid="deviation-typ-error">{errors.typ}</p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="dev-allvarlighetsgrad">Allvarlighetsgrad</Label>
        <select
          id="dev-allvarlighetsgrad"
          data-testid="deviation-allvarlighetsgrad-select"
          value={form.allvarlighetsgrad}
          onChange={e => setForm(f => ({ ...f, allvarlighetsgrad: e.target.value }))}
          className={selectCls}
        >
          {Object.entries(ALLVARLIGHET_CONFIG).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="dev-beskrivning">Beskrivning *</Label>
        <Textarea
          id="dev-beskrivning"
          data-testid="deviation-beskrivning-input"
          value={form.beskrivning}
          onChange={e => {
            setForm(f => ({ ...f, beskrivning: e.target.value }));
            if (errors.beskrivning) setErrors(er => ({ ...er, beskrivning: '' }));
          }}
          rows={3}
          placeholder="Beskriv vad som hänt..."
          className={errors.beskrivning ? 'border-destructive' : ''}
        />
        {errors.beskrivning && (
          <p className="text-xs text-destructive" data-testid="deviation-beskrivning-error">{errors.beskrivning}</p>
        )}
      </div>

      <div className="flex gap-3">
        <Button
          type="submit"
          data-testid="deviation-submit-btn"
          disabled={submitting}
          className="rounded-full font-bold"
        >
          {submitting ? 'Sparar...' : 'Registrera avvikelse'}
        </Button>
        <Button
          type="button"
          data-testid="deviation-cancel-btn"
          variant="outline"
          onClick={onClose}
          className="rounded-full"
        >
          Avbryt
        </Button>
      </div>
    </form>
  );
};

// ── Inline detail / edit ───────────────────────────────────────────────────────

const InlineDetail = ({ deviation, onUpdated, onClose }) => {
  const [status, setStatus]   = useState(deviation.status);
  const [atgard, setAtgard]   = useState(deviation.atgardsbeskrivning || '');
  const [saving, setSaving]   = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/avvikelser/${deviation.id}`, {
        status,
        atgardsbeskrivning: atgard || null,
      });
      toast.success('Avvikelse uppdaterad');
      onUpdated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte uppdatera avvikelse');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" onClick={e => e.stopPropagation()}>
      {/* Read-only metadata */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Typ</p>
          <p className="font-medium">{TYP_LABELS[deviation.typ] || deviation.typ}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Allvarlighetsgrad</p>
          <Badge config={ALLVARLIGHET_CONFIG[deviation.allvarlighetsgrad] || { label: deviation.allvarlighetsgrad, className: 'bg-muted' }} />
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Rapporterad</p>
          <p className="font-medium">{formatDate(deviation.rapporterad_datum)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Åtgärdad</p>
          <p className="font-medium">{formatDate(deviation.atgardad_datum)}</p>
        </div>
      </div>

      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Beskrivning</p>
        <p className="text-sm leading-relaxed bg-muted/40 rounded-md p-3">{deviation.beskrivning}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor={`dev-status-${deviation.id}`}>Status</Label>
          <select
            id={`dev-status-${deviation.id}`}
            data-testid={`deviation-status-select-${deviation.id}`}
            value={status}
            onChange={e => setStatus(e.target.value)}
            className={selectCls}
          >
            {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`dev-atgard-${deviation.id}`}>Åtgärdsbeskrivning</Label>
          <Textarea
            id={`dev-atgard-${deviation.id}`}
            data-testid={`deviation-atgard-textarea-${deviation.id}`}
            value={atgard}
            onChange={e => setAtgard(e.target.value)}
            rows={2}
            placeholder="Beskriv vidtagen åtgärd..."
          />
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          data-testid={`deviation-save-btn-${deviation.id}`}
          onClick={handleSave}
          disabled={saving}
          className="rounded-full font-bold"
        >
          {saving ? 'Sparar...' : 'Spara'}
        </Button>
        {status !== 'stangd' && (
          <Button
            variant="outline"
            onClick={() => setStatus('stangd')}
            className="rounded-full border-destructive text-destructive hover:bg-destructive/10"
          >
            Stäng avvikelse
          </Button>
        )}
        <Button
          variant="ghost"
          data-testid={`deviation-close-btn-${deviation.id}`}
          onClick={onClose}
          className="rounded-full ml-auto"
        >
          Stäng
        </Button>
      </div>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────

const DeviationsPage = () => {
  const { user } = useAuth();
  const canWrite = user?.role !== 'read_only_assigner' && user?.role !== 'read_only_participant';

  const [deviations, setDeviations]   = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [createOpen, setCreateOpen]   = useState(false);
  const [expandedId, setExpandedId]   = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTyp, setFilterTyp]       = useState('');
  const [filterFrom, setFilterFrom]     = useState('');
  const [filterTo, setFilterTo]         = useState('');

  // Escape closes create form
  useEffect(() => {
    if (!createOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setCreateOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [createOpen]);

  const fetchDeviations = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterTyp)    params.typ    = filterTyp;
      if (filterFrom)   params.from_date = filterFrom;
      if (filterTo)     params.to_date   = filterTo;
      const res = await api.get('/avvikelser', { params });
      setDeviations(res.data);
    } catch {
      toast.error('Kunde inte hämta avvikelser');
    }
  }, [filterStatus, filterTyp, filterFrom, filterTo]);

  useEffect(() => {
    const init = async () => {
      try {
        const pRes = await api.get('/participants');
        setParticipants(pRes.data);
      } catch { /* non-fatal */ }
      finally { setLoading(false); }
    };
    init();
  }, []);

  useEffect(() => { fetchDeviations(); }, [fetchDeviations]);

  const oppna = deviations.filter(d => d.status === 'oppen').length;
  const now     = new Date();
  const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
  const nyaDennaVecka = deviations.filter(d => new Date(d.rapporterad_datum) >= weekAgo).length;
  const atgardade = deviations.filter(d => d.atgardad_datum && d.rapporterad_datum);
  const avgTime = atgardade.length > 0
    ? Math.round(atgardade.reduce((acc, d) => {
        return acc + (new Date(d.atgardad_datum) - new Date(d.rapporterad_datum)) / (1000 * 60 * 60 * 24);
      }, 0) / atgardade.length)
    : null;

  const participantName = (id) => participants.find(p => p.id === id)?.name || id;

  const handleCreated = (dev) => setDeviations(prev => [dev, ...prev]);
  const handleUpdated = (dev) => setDeviations(prev => prev.map(d => d.id === dev.id ? dev : d));

  const toggleExpand = (devId) => setExpandedId(cur => (cur === devId ? null : devId));

  return (
    <DashboardLayout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Avvikelser</h1>
            <p className="text-muted-foreground mt-2">Registrera och följ upp avvikelser för deltagare</p>
          </div>
          {canWrite && (
            <Button
              data-testid="create-deviation-btn"
              onClick={() => setCreateOpen(v => !v)}
              className="rounded-full font-bold"
            >
              {createOpen ? <X size={18} className="mr-2" /> : <Plus size={18} className="mr-2" />}
              {createOpen ? 'Stäng' : 'Ny avvikelse'}
            </Button>
          )}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KpiCard label="Totalt öppna" value={oppna} />
          <KpiCard label="Nya denna vecka" value={nyaDennaVecka} />
          <KpiCard
            label="Genomsnittlig åtgärdstid"
            value={avgTime !== null ? `${avgTime} dagar` : '—'}
            sub={atgardade.length > 0 ? `Baserat på ${atgardade.length} åtgärdade` : 'Inga åtgärdade än'}
          />
        </div>

        {/* Inline create form */}
        {createOpen && (
          <Card className="border-primary/30" style={{ background: 'rgba(249,207,63,0.05)' }}>
            <CardContent className="p-5">
              <h2 className="text-lg font-heading font-bold mb-4">Registrera avvikelse</h2>
              <InlineCreateForm
                participants={participants}
                onCreated={handleCreated}
                onClose={() => setCreateOpen(false)}
              />
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Alla statusar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla statusar</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([k, { label }]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterTyp || 'all'} onValueChange={v => setFilterTyp(v === 'all' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Alla typer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla typer</SelectItem>
                  {Object.entries(TYP_LABELS).map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div>
                <Label className="text-xs text-muted-foreground">Från datum</Label>
                <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Till datum</Label>
                <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} />
              </div>
            </div>
            {(filterStatus || filterTyp || filterFrom || filterTo) && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 text-muted-foreground"
                onClick={() => { setFilterStatus(''); setFilterTyp(''); setFilterFrom(''); setFilterTo(''); }}
              >
                <X size={14} className="mr-1" /> Rensa filter
              </Button>
            )}
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">Laddar avvikelser...</CardContent>
          </Card>
        ) : deviations.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <AlertTriangle size={40} className="mx-auto mb-3 text-muted-foreground/30" />
              <p className="text-muted-foreground text-lg">Inga avvikelser hittades.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {deviations.map(dev => (
              <div key={dev.id}>
                {/* Summary card */}
                <Card
                  data-testid={`deviation-card-${dev.id}`}
                  className={`cursor-pointer hover:shadow-md transition-shadow ${expandedId === dev.id ? 'border-primary/50 rounded-b-none' : ''}`}
                  onClick={() => toggleExpand(dev.id)}
                >
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-bold text-sm">{participantName(dev.participant_id)}</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-sm">{TYP_LABELS[dev.typ] || dev.typ}</span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{dev.beskrivning}</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatDate(dev.rapporterad_datum)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Badge config={ALLVARLIGHET_CONFIG[dev.allvarlighetsgrad] || { label: dev.allvarlighetsgrad, className: 'bg-muted' }} />
                      <Badge config={STATUS_CONFIG[dev.status] || { label: dev.status, className: 'bg-muted' }} />
                    </div>
                    <ChevronDown
                      size={16}
                      className={`text-muted-foreground transition-transform shrink-0 ${expandedId === dev.id ? 'rotate-180' : ''}`}
                    />
                  </CardContent>
                </Card>

                {/* Inline detail */}
                {expandedId === dev.id && (
                  <Card
                    data-testid={`deviation-detail-${dev.id}`}
                    className="rounded-t-none border-t-0 border-primary/30"
                    style={{ background: 'rgba(249,207,63,0.05)' }}
                  >
                    <CardContent className="p-5">
                      <InlineDetail
                        deviation={dev}
                        onUpdated={handleUpdated}
                        onClose={() => setExpandedId(null)}
                      />
                    </CardContent>
                  </Card>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DeviationsPage;
